import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormValidation from '../../../../../hooks/useFormValidation';
import { EditSubstanceBody, useEditCarcinogenicSubstanceMutation } from '../../../../../services/carcinogenicApi';
import useEditSubstanceFieldsConfig, { BOOLEAN_OPTIONS } from './useEditSubstanceFieldsConfig';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import { enqueueSnackbar } from 'notistack';

export interface EditSubstanceFormData {
    substance: string | null;
    workPositionAndSector: string | null;
    comercialName: string | null;
    usageOrigin: { id: number; descripcion: string }[];
    employmentMethods: { id: number; descripcion: string }[];
    substanceAnualQuantity: string | null;
    measureUnit: string | null;
    report: null | { id: number; value: boolean; nombre: string };
    trainingProgram: null | { id: number; value: boolean; nombre: string };
    studiesAndReplacementPosibility: string | null;
    specialLicense: null | { id: number; value: boolean; nombre: string };
    preventiveMeasures: { id: number; descripcion: string }[];
    protectiveEquipment: { id: number; descripcion: string }[];
}

const INITIAL_VALUES: EditSubstanceFormData = {
    substance: '',
    workPositionAndSector: '',
    comercialName: '',
    usageOrigin: [],
    employmentMethods: [],
    substanceAnualQuantity: '',
    measureUnit: '',
    report: null,
    trainingProgram: null,
    studiesAndReplacementPosibility: '',
    specialLicense: null,
    preventiveMeasures: [],
    protectiveEquipment: []
};

const getMatches = (array: any[], ids: number[]) => {
    if (!array) return [];
    return array.filter((item) => ids.includes(item.id));
};

const getOptionByBooleanValue = (value: boolean) => {
    return BOOLEAN_OPTIONS.find((option) => option.value === value) || '';
};

const getInitialData = (selectedSubstance: any, carcinogenicParametricData: any): EditSubstanceFormData => {
    if (!selectedSubstance) return INITIAL_VALUES;
    return {
        substance: '',
        workPositionAndSector: '',
        comercialName: selectedSubstance?.nombreComercial || '',
        usageOrigin: getMatches(
            carcinogenicParametricData?.usos || [],
            selectedSubstance?.idsUso || []
        ),
        employmentMethods: getMatches(
            carcinogenicParametricData?.modosEmpleo || [],
            selectedSubstance?.idsModoEmpleo || []
        ),
        substanceAnualQuantity: selectedSubstance?.cantidadAnual || '',
        measureUnit: selectedSubstance?.idUnidadMedida || '',
        report: getOptionByBooleanValue(selectedSubstance?.informe) || null,
        trainingProgram: getOptionByBooleanValue(selectedSubstance?.capacitacion) || null,
        studiesAndReplacementPosibility: selectedSubstance?.estudiosRemplazo || '',
        specialLicense: getOptionByBooleanValue(selectedSubstance?.licenciaEspecial) || null,
        preventiveMeasures: getMatches(
            carcinogenicParametricData?.medidasPrevencion || [],
            selectedSubstance?.idsMedidaPrevencion || []
        ),
        protectiveEquipment: getMatches(
            carcinogenicParametricData?.elementosProteccion || [],
            selectedSubstance?.idsElementoProteccion || []
        )
    };
};
const useEditSubstanceForm = ({
    selectedSubstance,
    selectedSubstanceId,
    selectedCarcinogenicReportId,
    handleTabChange
}: {
    selectedSubstance: any;
    selectedSubstanceId: number | null;
    selectedCarcinogenicReportId: number | null;
    handleTabChange: any;
}) => {
    const [noComercialNameChecked, setNoComercialNameChecked] = useState<boolean>(selectedSubstance?.nombreComercial === 'No Aplica');

    const carcinogenicParametricData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicParametricData
    );

    const carcinogenicReport = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicReport
    );

    //this help to maintaing real time values without depending on local management
    const currentSubstanceItem = carcinogenicReport.sustancias.find(
        (item: any) => item?.id === selectedSubstance.id
    );

    const initialData = getInitialData(currentSubstanceItem, carcinogenicParametricData);
    const [formData, setFormData] = useState<EditSubstanceFormData>({
        ...initialData,
        substance: selectedSubstance?.descripcion,
        workPositionAndSector: `${selectedSubstance?.nombrePuesto} - ${selectedSubstance?.nombreSector}`
    });

    const [editSubstance, { isLoading: isEditingSubstance }] =
        useEditCarcinogenicSubstanceMutation();

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.substancesAndPreventiveMeasures'
    });

    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();

    const { editSubstanceFieldsConfig } = useEditSubstanceFieldsConfig({
        t,
        editSubstanceFormData: formData,
        selectedSubstance,
        handleBlurValidation,
        noComercialNameChecked
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleEditSubstance = async () => {
        if (!selectedSubstanceId || !selectedCarcinogenicReportId) return;
        if (!validateForm(formData, editSubstanceFieldsConfig)) return;

        try {
            const initialAdaptedBody: EditSubstanceBody = {
                nombreComercial: formData?.comercialName ?? '',
                usos:
                    (formData?.usageOrigin && formData?.usageOrigin?.map((usage) => usage.id)) ||
                    [],
                modosEmpleo: formData.employmentMethods?.map((method) => method.id) || [],
                cantidadAnual: Number(formData?.substanceAnualQuantity),
                idUnidadMedida: Number(formData?.measureUnit),
                informaRiesgo: formData?.report?.value === true,
                capacitaRiesgo: formData?.trainingProgram?.value === true,
                elementosProteccion: formData?.protectiveEquipment?.map(
                    (protectiveElement) => protectiveElement.id
                ),
                medidasPrevencion: formData?.preventiveMeasures?.map(
                    (preventiveMeasure) => preventiveMeasure.id
                ),
                estudiosRemplazo: formData?.studiesAndReplacementPosibility ?? '',
                licenciaEspecial: formData?.specialLicense?.value === true
            };

            const adaptedBody = formData?.substanceAnualQuantity
                ? initialAdaptedBody
                : { ...initialAdaptedBody, cantidadAnual: null, idUnidadMedida: null };

            const response: any = await editSubstance({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedSubstanceId,
                body: adaptedBody
            });

            if(response?.error) {
                enqueueSnackbar(t('newStudyForm.enqueues.editSubstance.error'), {
                    variant: 'error'
                });
                return
            }
            handleTabChange(null, 1);
        } catch (error) {
            enqueueSnackbar(t('newStudyForm.enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    return {
        formData,
        errors,
        editSubstanceFieldsConfig,
        isEditingSubstance,
        noComercialNameChecked,
        setNoComercialNameChecked,
        handleChange,
        validateForm,
        handleEditSubstance
    };
};

export default useEditSubstanceForm;
